/**
 * Formatação para exibição.
 *
 * ---
 *
 * O FUSO É O DA BARBEARIA, NÃO O DO CELULAR
 *
 * Um cliente viajando com o celular em outro fuso não pode ver o horário dele
 * deslocado — o corte é às 15h na barbearia, e é isso que a tela precisa dizer.
 * Toda função daqui recebe o `timeZone` do tenant, e o `Intl` faz a conversão
 * sem precisar de biblioteca de datas no bundle.
 */

export interface LocaleOptions {
  readonly locale: string;
  readonly timeZone: string;
  readonly currency?: string;
}

const DEFAULTS = { locale: 'pt-BR', timeZone: 'America/Sao_Paulo', currency: 'BRL' } as const;

/** Centavos → "R$ 45,00". Nunca fazemos aritmética de dinheiro em ponto flutuante. */
export function formatMoney(cents: number, options?: Partial<LocaleOptions>): string {
  return new Intl.NumberFormat(options?.locale ?? DEFAULTS.locale, {
    style: 'currency',
    currency: options?.currency ?? DEFAULTS.currency,
  }).format(cents / 100);
}

function parse(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/** "15:30" no fuso da barbearia. */
export function formatTime(value: string | Date, options?: Partial<LocaleOptions>): string {
  return new Intl.DateTimeFormat(options?.locale ?? DEFAULTS.locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: options?.timeZone ?? DEFAULTS.timeZone,
  }).format(parse(value));
}

/** "sáb, 14 de março". */
export function formatDate(value: string | Date, options?: Partial<LocaleOptions>): string {
  return new Intl.DateTimeFormat(options?.locale ?? DEFAULTS.locale, {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
    timeZone: options?.timeZone ?? DEFAULTS.timeZone,
  }).format(parse(value));
}

/** "sáb, 14 de março às 15:30". */
export function formatDateTime(value: string | Date, options?: Partial<LocaleOptions>): string {
  return `${formatDate(value, options)} às ${formatTime(value, options)}`;
}

/**
 * "hoje", "amanhã" ou a data — comparando no fuso da BARBEARIA.
 *
 * Comparar em UTC erraria por horas perto da meia-noite: um horário das 22h de
 * hoje em São Paulo é 1h de amanhã em UTC, e a tela diria "amanhã" para quem
 * tem corte marcado hoje à noite.
 */
export function formatRelativeDay(value: string | Date, options?: Partial<LocaleOptions>): string {
  const timeZone = options?.timeZone ?? DEFAULTS.timeZone;
  const dia = (date: Date): string =>
    new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'short' }).format(date);

  const alvo = parse(value);
  const hoje = new Date();
  const amanha = new Date(hoje.getTime() + 86_400_000);

  if (dia(alvo) === dia(hoje)) return 'hoje';
  if (dia(alvo) === dia(amanha)) return 'amanhã';

  return formatDate(alvo, options);
}

/** `2026-03-14` no fuso indicado, que é o formato que a API espera em `date`. */
export function toISODate(value: Date, timeZone: string = DEFAULTS.timeZone): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'short' }).format(value);
}

/** Duração em minutos → "1h 30min". */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;

  const horas = Math.floor(minutes / 60);
  const resto = minutes % 60;

  return resto === 0 ? `${horas}h` : `${horas}h ${resto}min`;
}

/**
 * Máscara progressiva de telefone brasileiro, aplicada enquanto o cliente
 * digita. O servidor normaliza para E.164 — aqui é só legibilidade.
 */
export function maskPhoneInput(value: string): string {
  const digitos = value.replace(/\D/g, '').slice(0, 11);

  if (digitos.length <= 2) return digitos;
  if (digitos.length <= 6) return `(${digitos.slice(0, 2)}) ${digitos.slice(2)}`;
  if (digitos.length <= 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  }

  return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
}

/** Só os dígitos — o que vai para a API. */
export function unmaskPhone(value: string): string {
  return value.replace(/\D/g, '');
}

/** Primeiro nome, para a saudação. */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** "Bom dia" / "Boa tarde" / "Boa noite", pela hora da BARBEARIA. */
export function greeting(timeZone: string = DEFAULTS.timeZone, now: Date = new Date()): string {
  const hora = Number(
    new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', hour12: false }).format(now),
  );

  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
}

/**
 * O caminho INVERSO: o que a pessoa digita vira centavos inteiros.
 *
 * Vive aqui, ao lado de `formatMoney`, porque os dois painéis administrativos
 * precisam dele e porque as duas metades da conversão têm que combinar. Separá-
 * las garantiria que uma aceitasse vírgula e a outra não.
 *
 * O arredondamento não é detalhe: `12.345 * 100` em ponto flutuante dá
 * 1234.4999999999998, e o preço salvo ficaria um centavo abaixo do digitado.
 */
export function centsFromInput(valor: string): number {
  const limpo = valor.replace(/[^\d,.-]/g, '').replace(',', '.');
  const numero = Number.parseFloat(limpo);

  return Number.isFinite(numero) ? Math.round(numero * 100) : 0;
}

/** Centavos → "129,90", para preencher um campo de digitação. */
export function inputFromCents(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}
